import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api, fmtINR, fmtDateTime } from "@/lib/api";
import { maskMobile } from "@/lib/membership";
import StatTile from "@/components/StatTile";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  PlusCircle,
  ShoppingCart,
  IndianRupee,
  Coins,
  Clock,
  ArrowRight,
  CalendarDays,
  Percent,
} from "lucide-react";

export default function AgentDashboard() {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    refreshUser().catch(() => {});
    api.get("/agent/me/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/agent/me/memberships?limit=8").then((r) => setRecent(r.data)).catch(() => {});
  }, []);

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-1.5">
            Agent dashboard
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            Hi {user?.name?.split(" ")[0]}, ready to sell?
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every ₹100 card you sell earns you{" "}
            <span className="font-semibold text-foreground">{user?.commission_rate}%</span> commission.
          </p>
        </div>
        <Link to="/agent/new">
          <Button size="lg" className="h-11 bg-gradient-primary text-primary-foreground shadow-elegant rounded-xl">
            <PlusCircle className="h-4 w-4" /> Sell new card
          </Button>
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatTile
          icon={ShoppingCart}
          label="Cards sold"
          value={stats?.cards_sold ?? "—"}
          hint={`${stats?.sales_today ?? 0} today · ${stats?.sales_this_month ?? 0} this month`}
          accent="primary"
        />
        <StatTile
          icon={IndianRupee}
          label="Revenue generated"
          value={fmtINR(stats?.total_revenue)}
          hint="At ₹100 per card"
          accent="accent"
        />
        <StatTile
          icon={Coins}
          label="Total commission"
          value={fmtINR(stats?.total_commission)}
          hint={`Your rate · ${user?.commission_rate}%`}
          accent="success"
        />
        <StatTile
          icon={Clock}
          label="Pending payout"
          value={fmtINR(stats?.pending_commission)}
          hint={`Paid out: ${fmtINR(stats?.paid_commission)}`}
          accent="warning"
        />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 border-border/70 shadow-soft">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Recent sales
                </div>
                <div className="font-display font-semibold text-lg text-foreground mt-0.5">
                  Your latest cards
                </div>
              </div>
              <Link to="/agent/sales" className="text-sm font-medium text-primary inline-flex items-center gap-1 hover:underline">
                View all <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            {recent.length === 0 ? (
              <div className="py-12 text-center">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary mb-3">
                  <PlusCircle className="h-6 w-6" />
                </span>
                <div className="font-semibold text-foreground">No sales yet</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Sell your first card to start earning commission.
                </p>
                <Link to="/agent/new">
                  <Button className="mt-4 bg-gradient-primary text-primary-foreground rounded-lg">
                    <PlusCircle className="h-4 w-4" /> Sell first card
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {recent.map((m) => (
                  <div key={m.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground truncate">
                        {m.patient_name}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono-card tracking-wider">
                        {m.membership_number} · {maskMobile(m.patient_mobile)}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold text-foreground">+{fmtINR(m.commission_amount)}</div>
                      <div className="text-[11px] text-muted-foreground">{fmtDateTime(m.issued_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary/25 bg-gradient-to-br from-primary/8 to-accent/8 shadow-soft">
          <CardContent className="p-5 sm:p-6 flex flex-col h-full">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
              How you earn
            </div>
            <div className="font-display font-semibold text-lg text-foreground mt-0.5">
              Simple, transparent commission
            </div>
            <ul className="mt-5 space-y-3 text-sm text-foreground">
              <li className="flex items-start gap-3">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15 text-primary mt-0.5">
                  <Percent className="h-3.5 w-3.5" />
                </span>
                <span>
                  <span className="font-semibold">{user?.commission_rate}% of ₹100</span> per
                  successful sale =&nbsp;
                  <span className="font-semibold">{fmtINR((user?.commission_rate * 100) / 100)}</span> earned.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15 text-primary mt-0.5">
                  <CalendarDays className="h-3.5 w-3.5" />
                </span>
                <span>Commissions are reconciled monthly and paid by your administrator.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15 text-primary mt-0.5">
                  <Coins className="h-3.5 w-3.5" />
                </span>
                <span>You see real-time pending vs paid breakdown on this dashboard.</span>
              </li>
            </ul>
            <div className="mt-auto pt-6">
              <Link to="/agent/new">
                <Button className="w-full bg-gradient-primary text-primary-foreground rounded-xl shadow-elegant">
                  <PlusCircle className="h-4 w-4" /> Start a new sale
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
